/**
 * User: n.gorte
 * Date: 28.06.2022
 * Time: 14:19
 */
/**
 * Created by Carsten Bühler
 * User: CBuehler on 05.09.2016
 * Copyright: xdot GmbH 2015
 */
'use strict';

import L from 'external/leaflet';
import React from 'external/react';
import ReactDOM from 'external/react-dom';
import { debounce, deepEqual } from 'plugin/liferay-widgets-react-web';
import '../css/search-plugin.scss';
import { SearchBox } from './SearchPlugin/SearchBox';
import { SearchBoxAutocomplete } from './SearchPlugin/SearchBoxAutocomplete';
import { Client } from 'external/lwr-client';
import { loopLogicalLayers } from '../utils/map-config-util';

const SUGGEST_URL = 'https://geo.lupo-cloud.de/bkg_osgts_adv/suggest?count=5&filter=typ:ort OR typ:strasse&query=';
const GEOSEARCH_URL = 'https://geo.lupo-cloud.de/bkg_osgts_adv/geosearch/?srsName=EPSG:4326&format=json&count=1&filter=';
const searchGeoFeatures = (query, classNames, gaugingStationTypeKeys) => Client.invoke('/geo.geofeature/search-geo-features.6', {
    classNames,
    gaugingStationTypeKeys,
    limit: 5,
    searchTerm: query,
    groupId: Client.getScopeGroupId(),
});

export default class SearchPlugin {

    constructor(props, config) {
        this.config = config;
        this.props = props;

        this.suggestions = {
            type: 'suggestions',
            items: [],
            error: null
        };

        this.fliwasObjects = {
            type: 'fliwas',
            items: [],
            error: null
        };

        this.selectedPlace = {
            type: 'place',
            item: null,
            error: null
        };

        this.query = '';

        this.onInputChange = this.onInputChange.bind(this);
        this.resetItems = this.resetItems.bind(this);
        this.onInputFocus = this.onInputFocus.bind(this);
        this.renderAutoComplete = this.renderAutoComplete.bind(this);
        this.onFliwasObjectClick = this.onFliwasObjectClick.bind(this);
        this.onSuggestionClick = this.onSuggestionClick.bind(this);
        this.onResize = this.onResize.bind(this);
        this.search = debounce(this.search, 300);
        this.onClose = this.onClose.bind(this);
    }

    init(map, o, reEvaluate) {
        this._reEvaluate = reEvaluate;
        this._map = map;
        this._options = o;

        this.placeMarker = L.marker().bindPopup();
        this.searchControl = new SearchBox();
        map.addControl(this.searchControl);
        this.searchControl.onInput('input', this.onInputChange);
        this.searchControl.onInput('focus', this.onInputChange);
        this.searchControl.onClear('click', this.resetItems);

        this._map.on('resize', this.onResize);
        this.onResize();
    }

    update(map, o) {
        if (!deepEqual(this._options.activeUuids, o.activeUuids)) {
            this.search(this.query);
        }
        this._options = o;
    }

    remove() {
        this._map.off();
        this.placeMarker && this.placeMarker.remove();
        if (this.searchControl) {
            this.searchControl.remove();
        }
        this._map = null;
    }

    onResize() {
        if (this._map.getSize().x >= 600) {
            this.searchControl.show();
        }
    }

    onInputChange(e) {
        const value = e.target.value;
        this.query = value;
        this.search(value);
    }

    onInputFocus() {
        if (this.fliwasObjects.items.length > 0) {
            this.renderAutoComplete();
        }
    }

    resetItems() {
        this.placeMarker && this.placeMarker.remove();
        this.query = '';
        this.suggestions = {
            type: 'suggestions',
            items: [],
            error: null
        };
        this.fliwasObjects = {
            type: 'fliwas',
            items: [],
            error: null
        };
        ReactDOM.unmountComponentAtNode(this.searchControl._autocompleteWrapper);
    }

    search(query) {
        let { activeUuids, layerDescriptions } = this._options;
        const layerDescriptionsFlat = layerDescriptions && layerDescriptions.map(ld => loopLogicalLayers(ld, [])).flat();

        const options = layerDescriptionsFlat && layerDescriptionsFlat
            .filter(l => activeUuids[l.uuid])
            .reduce((acc, layerDescription) => {
                try {
                    const configString = layerDescription.children && layerDescription.children[0].config;
                    const configJson = layerDescription.config || JSON.parse(configString);
                    if (configJson.className) {
                        if (!acc.classNames.includes(configJson.className)) {
                            acc.classNames.push(configJson.className);
                        }
                        if (configJson.className === 'GaugingStation' && configJson.typeKey) {
                            acc.gaugingStationTypeKeys.push(configJson.typeKey);
                        }
                    }
                } catch (e) {
                    console.error('Failed to parse config of: ', layerDescription, e);
                }
                return acc;
            }, {
                classNames: [],
                gaugingStationTypeKeys: []
            });

        if (query.length > 2) {
            this.getSuggestions(query);
            this.getFliwasObjects(query, [ ...options.classNames ], options.gaugingStationTypeKeys);
        }
    }

    getFliwasObjects(query, classNames, gaugingStationTypeKeys) {
        if (!classNames.length > 0) {
            this.fliwasObjects = {
                ...this.fliwasObjects,
                items: [],
            };
            return;
        }
        searchGeoFeatures(query, classNames, gaugingStationTypeKeys)
            .then((json) => {
                this.fliwasObjects = {
                    ...this.fliwasObjects,
                    items: json,
                    error: null,
                };
            })
            .catch((error) => {
                this.fliwasObjects = {
                    ...this.fliwasObjects,
                    items: [],
                    error,
                };
            })
            .finally(this.renderAutoComplete);
    }

    getSuggestions(query) {
        const suggestUrl = encodeURI(SUGGEST_URL + query);
        fetch(suggestUrl)
            .then((response) => response.json())
            .then((json) => {
                this.suggestions = {
                    ...this.suggestions,
                    items: json,
                    error: null,
                };
            })
            .catch((error) => {
                this.suggestions = {
                    ...this.suggestions,
                    items: [],
                    error,
                };
            })
            .finally(this.renderAutoComplete);
    }

    getPlace(suggestion) {
        const queryString = suggestion.suggestion.replace(' ', '+');
        const geosearchUrl = encodeURI(`${GEOSEARCH_URL}typ:${suggestion.type}&query=${queryString}`);
        fetch(geosearchUrl)
            .then((response) => response.json())
            .then((json) => {
                this.selectedPlace = {
                    ...this.selectedPlace,
                    item: json.features[0],
                    error: null,
                };
                this.setPlaceMarker(json.features[0]);
            })
            .catch((error) => {
                this.selectedPlace = {
                    ...this.selectedPlace,
                    item: null,
                    error,
                };
            });
    }

    onClose() {
        this.resetItems();
        ReactDOM.unmountComponentAtNode(this.searchControl._autocompleteWrapper);
    }

    onSuggestionClick(suggestion) {
        this.searchControl.setValue(suggestion.suggestion);
        this.getPlace(suggestion);
        this.onClose();
    }

    onFliwasObjectClick(fliwasObject) {
        const { properties, geometry } = fliwasObject;
        const { id, type } = properties;
        const { coordinates, type: geometryType } = geometry;
        if (geometryType !== 'Point') {
            const latLngs = L.geoJSON(geometry).getBounds();
            this._map.fitBounds(latLngs);
        } else {
            const latLng = L.latLng(coordinates[1], coordinates[0]);
            this._map.setView(latLng, 14);
        }

        // global selection not required FLIWASEN-980 Auswahl von Objekten wird auf alle Karten übertragen
        //RemoteDataActions.pageSelected(type, { id });
        this.onClose();
    }

    setPlaceMarker(feature) {
        const { geometry } = feature;
        const { coordinates } = geometry;
        const latLng = L.latLng(coordinates[1], coordinates[0]);

        this.placeMarker.setLatLng(latLng);
        if (!this._map.hasLayer(this.placeMarker)) {
            this.placeMarker.addTo(this._map);
        }

        this.placeMarker.setPopupContent(feature.properties.text);
        this.placeMarker.openPopup();
        this._map.setView(latLng, 13);
    }

    renderAutoComplete() {
        ReactDOM.render(<SearchBoxAutocomplete
                suggestions={this.suggestions}
                fliwasObjects={this.fliwasObjects}
                onSuggestionClick={this.onSuggestionClick}
                onFliwasObjectClick={this.onFliwasObjectClick}
                activeUuids={this._options.activeUuids}
                onClose={this.onClose}
            />,
            this.searchControl._autocompleteWrapper);
    }

}



// WEBPACK FOOTER //
// ./js/plugins/SearchPlugin.js